import {useCallback, useContext} from 'react';

import {useValidateProfile} from 'src/admin-rights-service/modules/useValidateProfile';
import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {createAccessRightsProfiles} from 'src/openapi/admin/apis/CreateAccessRightsProfilesApi';
import {getAccessRightProfile} from 'src/openapi/admin/apis/GetAccessRightProfileApi';
import {useAppDispatch} from 'src/store/hooks';

import {INIT_PROFILE} from '../constants';

import {getDefaultName} from './utils';

import type {AccessRightsProfile} from 'src/openapi/admin/models/AccessRightsProfile';

type Props = {
    duplicateProfileId?: number,
};

export const useNewRoleProfile = ({duplicateProfileId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {validateProfileName, validateDescription, validatePermissions} = useValidateProfile();
    const {getErrorsByControlId} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        if (duplicateProfileId) {
            const response = await apiDispatch(getAccessRightProfile({profileId: duplicateProfileId}));

            if (response.payload.success) {
                const {data} = response.payload;
                const initialState: {success: true, error: false, data: AccessRightsProfile} = {
                    success: true,
                    error: false,
                    data: {
                        ...INIT_PROFILE,
                        profileName: getDefaultName(data.profileName),
                        rightsCodes: data.rightsCodes,
                        profileDescription: data.profileDescription,
                    },
                };

                return Promise.resolve({
                    type: '',
                    payload: initialState,
                });
            } else {
                return response;
            }
        } else {
            const initialState: {success: true, error: false, data: AccessRightsProfile} = {
                success: true,
                error: false,
                data: {...INIT_PROFILE, profileName: getDefaultName('New Role')},
            };

            return Promise.resolve({
                type: 'GET_ACCESS_RIGHT_PROFILE__GET_ACCESS_RIGHT_PROFILE__SUCCESS',
                payload: initialState,
            });
        }
    }, [duplicateProfileId, apiDispatch]);

    const saveDispatchAction = useCallback((roleProfile: AccessRightsProfile) => apiDispatch(
        createAccessRightsProfiles({profileSaveRequest: {
            profileName: roleProfile.profileName,
            profileDescription: roleProfile.profileDescription || '',
            rightsCodes: roleProfile.rightsCodes,
            notes: 'Profile created',
        }}),
    ), [apiDispatch]);

    const saveValidation = useCallback((roleProfile: AccessRightsProfile) => ![
        getErrorsByControlId('profile-name').length,
        validateDescription(roleProfile.profileDescription),
        validatePermissions(roleProfile.rightsCodes),
    ].some(Boolean), [getErrorsByControlId, validateDescription, validatePermissions]);

    return ({
        ...useCommonDataHook({
            saveDispatchAction,
            getDispatchAction,
            saveValidation,
            initValue: INIT_PROFILE,
        }),
        validateProfileName,
        validateDescription,
        validatePermissions,
    });
};
